import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
	MagnifyingGlassIcon,
	MapPinIcon,
	EnvelopeIcon,
	PhoneIcon,
	ExclamationTriangleIcon,
	CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Button, Badge, Modal, Spinner } from '../ui';
import { staffNetworkApi } from '../../api/staffNetwork';
import { ApiError } from '../../api';
import type {
	NetworkAreas,
	RotationStaff,
	StaffInvite,
	StaffInviteRequest,
} from '../../types/staffNetwork';

export interface RotationStaffFinderProps {
	/** The location selected in the header; the default location on an invite. */
	locationId: number | null;
	/** The provider's own city/state, to open the search nearby. */
	homeCity: string | null;
	homeState: string | null;
	locations: {
		id: number;
		locationName: string | null;
		address: string;
		city: string;
		state: string;
		active: boolean;
	}[];
}

const TRAVEL_MINUTES = 30;

const AVATAR_COLORS = [
	'#667eea', '#764ba2', '#f59e0b', '#10b981', '#ec4899', '#3b82f6',
	'#8b5cf6', '#06b6d4', '#f97316', '#84cc16', '#a855f7', '#14b8a6',
];
const avatarColor = (id: number) => AVATAR_COLORS[id % AVATAR_COLORS.length];
const initials = (first: string, last: string) =>
	`${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();

const fmtDate = (iso: string | null): string => {
	if (!iso) return '';
	const [y, m, d] = iso.split('-').map(Number);
	return new Date(y, m - 1, d).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
};

const inAWeek = (): string => {
	const d = new Date();
	d.setDate(d.getDate() + 7);
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
		d.getDate()
	).padStart(2, '0')}`;
};

const areaText = (s: RotationStaff) => s.areas.map(a => `${a.city}, ${a.state}`).join(' • ');

// ─── Invite modal ───────────────────────────────────────────────

interface InviteModalProps {
	isOpen: boolean;
	onClose: () => void;
	staff: RotationStaff | null;
	locationId: number | null;
	locations: RotationStaffFinderProps['locations'];
	onSent: (invite: StaffInvite) => void;
}

const InviteModal = ({ isOpen, onClose, staff, locationId, locations, onSent }: InviteModalProps) => {
	const [staffType, setStaffType] = useState<'ON_CALL' | 'PERMANENT'>('ON_CALL');
	const [startDate, setStartDate] = useState(inAWeek());
	const [rate, setRate] = useState('45');
	const [message, setMessage] = useState('');
	const [selectedLocationIds, setSelectedLocationIds] = useState<Set<number>>(new Set());
	const [isSending, setIsSending] = useState(false);

	useEffect(() => {
		if (isOpen) {
			setStaffType('ON_CALL');
			setStartDate(inAWeek());
			setRate('45');
			setMessage('');
			setSelectedLocationIds(new Set(locationId ? [locationId] : []));
		}
	}, [isOpen, locationId]);

	const activeLocations = useMemo(() => locations.filter(l => l.active), [locations]);
	const rateNum = parseFloat(rate);
	const rateValid = !Number.isNaN(rateNum) && rateNum >= 0 && rateNum <= 100;

	const toggleLocation = (id: number) => {
		setSelectedLocationIds(prev => {
			const next = new Set(prev);
			if (staffType === 'PERMANENT') {
				return new Set([id]);
			}
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const handleTypeChange = (type: 'ON_CALL' | 'PERMANENT') => {
		setStaffType(type);
		if (type === 'PERMANENT') {
			setSelectedLocationIds(prev => {
				const first = prev.values().next().value ?? locationId;
				return new Set(first != null ? [first] : []);
			});
		}
	};

	const send = async () => {
		if (!staff || !rateValid || !locationId) return;
		if (selectedLocationIds.size === 0) {
			toast.error('Choose at least one location.');
			return;
		}
		setIsSending(true);
		try {
			const payload: StaffInviteRequest = {
				homeStaffId: staff.homeStaffId,
				locationIds: Array.from(selectedLocationIds),
				staffType,
				commissionRate: rateNum,
				startDate,
				message: message.trim() || undefined,
			};
			const invite = await staffNetworkApi.sendInvite(locationId, payload);
			toast.success(
				`Invite sent to ${staff.firstName} ${staff.lastName} — ${rateNum}% ${
					staffType === 'ON_CALL' ? 'rotation' : 'permanent'
				}`
			);
			onSent(invite);
			onClose();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to send invite');
		} finally {
			setIsSending(false);
		}
	};

	if (!staff) return null;
	const others = staff.rotationAt.length;

	return (
		<Modal isOpen={isOpen} onClose={onClose} title={`Invite ${staff.firstName} ${staff.lastName}`} size="lg">
			<div className="space-y-5">
				<div className="flex items-center gap-3 rounded-xl border border-[#667eea] bg-indigo-50 dark:bg-indigo-900/20 px-4 py-3">
					<div
						className="w-10 h-10 rounded-lg text-white flex items-center justify-center text-sm font-bold flex-shrink-0"
						style={{ background: avatarColor(staff.homeStaffId) }}
					>
						{initials(staff.firstName, staff.lastName)}
					</div>
					<div className="min-w-0">
						<div className="font-semibold text-gray-900 dark:text-gray-100">
							{staff.firstName} {staff.lastName}
						</div>
						<div className="text-xs text-gray-500 dark:text-gray-400 truncate">
							{[staff.roleName, `${staff.jobsDone} jobs done`, areaText(staff)]
								.filter(Boolean)
								.join(' • ')}
						</div>
					</div>
				</div>

				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">Employment Type</label>
						<select
							className="provider-form__input"
							value={staffType}
							onChange={e => handleTypeChange(e.target.value as 'ON_CALL' | 'PERMANENT')}
						>
							<option value="ON_CALL">Rotation (On Call)</option>
							<option value="PERMANENT">Permanent</option>
						</select>
					</div>
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">Start Date</label>
						<input
							type="date"
							className="provider-form__input"
							value={startDate}
							onChange={e => setStartDate(e.target.value)}
						/>
					</div>
				</div>

				<div className="provider-form__group mb-0">
					<label className="provider-form__label">
						Commission Rate (%) <span className="provider-form__required">*</span>
					</label>
					<input
						type="number"
						className="provider-form__input"
						min={0}
						max={100}
						value={rate}
						onChange={e => setRate(e.target.value)}
					/>
					<div className="mt-3 grid grid-cols-2 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
						<div className="p-3 text-center border-r border-gray-200 dark:border-gray-700">
							<div className="text-xl font-bold text-indigo-700 dark:text-indigo-300">
								{rateValid ? `${rateNum}%` : '—'}
							</div>
							<div className="text-xs text-gray-500 dark:text-gray-400">Stylist keeps</div>
						</div>
						<div className="p-3 text-center">
							<div className="text-xl font-bold text-gray-800 dark:text-gray-100">
								{rateValid ? `${Math.round((100 - rateNum) * 100) / 100}%` : '—'}
							</div>
							<div className="text-xs text-gray-500 dark:text-gray-400">Salon keeps</div>
						</div>
					</div>
					{!rateValid && (
						<p className="text-xs text-red-500 mt-1">Enter a rate between 0 and 100.</p>
					)}
				</div>

				<div className="provider-form__group mb-0">
					<label className="provider-form__label">
						{staffType === 'PERMANENT' ? 'Location (one)' : 'Locations'}
					</label>
					<div className="flex flex-wrap gap-2">
						{activeLocations.map(l => {
							const on = selectedLocationIds.has(l.id);
							return (
								<button
									type="button"
									key={l.id}
									onClick={() => toggleLocation(l.id)}
									className={`px-3 py-1.5 rounded-full text-sm border cursor-pointer transition-colors ${
										on
											? 'bg-indigo-50 dark:bg-indigo-900/30 border-[#667eea] text-indigo-700 dark:text-indigo-300'
											: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-indigo-300'
									}`}
								>
									{l.locationName || l.address}
									{l.city ? ` · ${l.city}` : ''}
								</button>
							);
						})}
					</div>
				</div>

				<div className="provider-form__group mb-0">
					<label className="provider-form__label">Note (optional)</label>
					<textarea
						className="provider-form__input"
						rows={2}
						value={message}
						onChange={e => setMessage(e.target.value)}
						placeholder="e.g. We'd love your knotless work on Saturdays."
					/>
				</div>

				{others > 0 ? (
					<div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 px-4 py-3 text-sm">
						<div className="flex items-center gap-2 font-semibold text-amber-800 dark:text-amber-200">
							<ExclamationTriangleIcon className="w-5 h-5" /> Already works elsewhere
						</div>
						<p className="text-amber-700 dark:text-amber-300 mt-0.5">
							{staff.firstName} is on rotation at{' '}
							{staff.rotationAt.map(r => r.businessName).join('; ')}. Bookings here must leave{' '}
							<strong>{TRAVEL_MINUTES} minutes</strong> either side of any booking at another
							location.
						</p>
					</div>
				) : (
					<div className="rounded-xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800 px-4 py-3 text-sm">
						<div className="flex items-center gap-2 font-semibold text-emerald-800 dark:text-emerald-200">
							<CheckCircleIcon className="w-5 h-5" /> No other engagements
						</div>
						<p className="text-emerald-700 dark:text-emerald-300 mt-0.5">
							The travel rule won&apos;t restrict booking.
						</p>
					</div>
				)}

				<div className="flex justify-end gap-3 pt-2">
					<Button variant="secondary" onClick={onClose} disabled={isSending}>
						Cancel
					</Button>
					<Button variant="primary" onClick={send} isLoading={isSending} disabled={!rateValid}>
						Send Invite
					</Button>
				</div>
			</div>
		</Modal>
	);
};

// ─── Profile modal ──────────────────────────────────────────────

interface ProfileModalProps {
	isOpen: boolean;
	onClose: () => void;
	staff: RotationStaff | null;
	onInvite: (staff: RotationStaff) => void;
}

const ProfileModal = ({ isOpen, onClose, staff, onInvite }: ProfileModalProps) => {
	if (!staff) return null;
	const canInvite = !staff.alreadyOnTeam && !staff.pendingInviteId;
	return (
		<Modal isOpen={isOpen} onClose={onClose} title={`${staff.firstName} ${staff.lastName}`} size="md">
			<div className="space-y-5">
				<div className="flex items-center gap-4">
					<div
						className="w-14 h-14 rounded-xl text-white flex items-center justify-center text-lg font-bold flex-shrink-0"
						style={{ background: avatarColor(staff.homeStaffId) }}
					>
						{initials(staff.firstName, staff.lastName)}
					</div>
					<div>
						<div className="flex items-center gap-2 flex-wrap">
							<span className="text-lg font-bold text-gray-900 dark:text-gray-100">
								{staff.firstName} {staff.lastName}
							</span>
							<Badge variant="warning">Rotation</Badge>
						</div>
						<div className="text-sm text-gray-500 dark:text-gray-400">{staff.roleName}</div>
					</div>
				</div>

				<div className="grid grid-cols-2 gap-3">
					<div className="rounded-xl bg-gray-50 dark:bg-gray-800 p-3 text-center">
						<div className="text-xl font-bold text-gray-900 dark:text-gray-100">{staff.jobsDone}</div>
						<div className="text-xs text-gray-500 dark:text-gray-400">Jobs done</div>
					</div>
					<div className="rounded-xl bg-gray-50 dark:bg-gray-800 p-3 text-center">
						<div className="text-xl font-bold text-gray-900 dark:text-gray-100">{staff.salonCount}</div>
						<div className="text-xs text-gray-500 dark:text-gray-400">
							Salon{staff.salonCount === 1 ? '' : 's'} on BBP
						</div>
					</div>
				</div>

				<div className="divide-y divide-gray-100 dark:divide-gray-700 text-sm">
					<div className="flex justify-between gap-4 py-2">
						<span className="text-gray-500 dark:text-gray-400">Email</span>
						<span className="text-gray-900 dark:text-gray-100 text-right break-all">{staff.email ?? '—'}</span>
					</div>
					<div className="flex justify-between gap-4 py-2">
						<span className="text-gray-500 dark:text-gray-400">Phone</span>
						<span className="text-gray-900 dark:text-gray-100">{staff.phone ?? '—'}</span>
					</div>
					<div className="flex justify-between gap-4 py-2">
						<span className="text-gray-500 dark:text-gray-400">Works in</span>
						<span className="text-gray-900 dark:text-gray-100 text-right">{areaText(staff) || '—'}</span>
					</div>
				</div>

				<div>
					<p className="provider-form__label mb-2">On rotation at</p>
					<div className="space-y-2">
						{staff.rotationAt.map(r => (
							<div
								key={r.providerId}
								className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-2.5"
							>
								<div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white flex items-center justify-center text-xs font-bold">
									{r.businessName
										.split(' ')
										.map(w => w[0])
										.join('')
										.slice(0, 2)
										.toUpperCase()}
								</div>
								<div className="min-w-0 flex-1">
									<div className="font-medium text-gray-900 dark:text-gray-100">{r.businessName}</div>
									<div className="text-xs text-gray-500 dark:text-gray-400">
										{[r.city, r.state].filter(Boolean).join(', ')}
									</div>
								</div>
								<Badge variant="warning">Rotation</Badge>
							</div>
						))}
					</div>
				</div>

				<div className="flex justify-end gap-3 pt-2">
					<Button variant="secondary" onClick={onClose}>
						Close
					</Button>
					<Button variant="primary" disabled={!canInvite} onClick={() => onInvite(staff)}>
						{staff.alreadyOnTeam ? 'On your team' : staff.pendingInviteId ? 'Invite Sent' : 'Invite & Set Commission'}
					</Button>
				</div>
			</div>
		</Modal>
	);
};

// ─── Finder ─────────────────────────────────────────────────────

/**
 * Find Rotation Staff: stylists listed as rotation (on-call) staff on another salon's
 * team. Browse by state and city, or look someone up by name, email or phone, then
 * invite them with a commission rate. Nothing joins the team until they accept.
 */
const RotationStaffFinder = ({ locationId, homeCity, homeState, locations }: RotationStaffFinderProps) => {
	const [areas, setAreas] = useState<NetworkAreas | null>(null);
	const [state, setState] = useState('');
	const [city, setCity] = useState('');
	const [query, setQuery] = useState('');
	const [applied, setApplied] = useState<{ state: string; city: string; query: string } | null>(null);
	const [results, setResults] = useState<RotationStaff[]>([]);
	const [isSearching, setIsSearching] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [pendingInvites, setPendingInvites] = useState<StaffInvite[]>([]);
	const [withdrawingId, setWithdrawingId] = useState<number | null>(null);
	const [profileStaff, setProfileStaff] = useState<RotationStaff | null>(null);
	const [inviteStaff, setInviteStaff] = useState<RotationStaff | null>(null);

	// Areas + default to the salon's own state/city so nearby staff show first.
	useEffect(() => {
		let cancelled = false;
		staffNetworkApi
			.areas()
			.then(a => {
				if (cancelled) return;
				setAreas(a);
				const st = homeState && a.states.some(s => s.code === homeState.toUpperCase())
					? homeState.toUpperCase()
					: '';
				const stateAreas = a.states.find(s => s.code === st);
				const ct =
					st && homeCity && stateAreas?.cities.some(c => c.toLowerCase() === homeCity.toLowerCase())
						? stateAreas.cities.find(c => c.toLowerCase() === homeCity.toLowerCase()) ?? ''
						: '';
				setState(st);
				setCity(ct);
				setApplied({ state: st, city: ct, query: '' });
			})
			.catch(() => {
				if (!cancelled) {
					setAreas({ states: [] });
					setApplied({ state: '', city: '', query: '' });
				}
			});
		return () => {
			cancelled = true;
		};
	}, [homeCity, homeState]);

	const loadPending = useCallback(async () => {
		try {
			setPendingInvites(await staffNetworkApi.sentInvites('PENDING'));
		} catch {
			setPendingInvites([]);
		}
	}, []);

	useEffect(() => {
		loadPending();
	}, [loadPending]);

	// Run the search whenever the applied filters change.
	useEffect(() => {
		if (!applied) return;
		let cancelled = false;
		setIsSearching(true);
		setError(null);
		staffNetworkApi
			.search({ state: applied.state, city: applied.city, query: applied.query })
			.then(r => {
				if (!cancelled) setResults(r);
			})
			.catch(err => {
				if (!cancelled) setError(err instanceof Error ? err.message : 'Search failed');
			})
			.finally(() => {
				if (!cancelled) setIsSearching(false);
			});
		return () => {
			cancelled = true;
		};
	}, [applied]);

	const cities = useMemo(
		() => areas?.states.find(s => s.code === state)?.cities ?? [],
		[areas, state]
	);

	const apply = () => setApplied({ state, city, query: query.trim() });
	const searchEverywhere = () => {
		setState('');
		setCity('');
		setApplied({ state: '', city: '', query: query.trim() });
	};

	const markInvited = (invite: StaffInvite) => {
		setResults(prev =>
			prev.map(r => (r.homeStaffId === invite.homeStaffId ? { ...r, pendingInviteId: invite.id } : r))
		);
		setPendingInvites(prev => [invite, ...prev]);
	};

	const withdraw = async (invite: StaffInvite) => {
		setWithdrawingId(invite.id);
		try {
			await staffNetworkApi.withdrawInvite(invite.id);
			setPendingInvites(prev => prev.filter(i => i.id !== invite.id));
			setResults(prev =>
				prev.map(r => (r.homeStaffId === invite.homeStaffId ? { ...r, pendingInviteId: null } : r))
			);
			toast.success('Invite withdrawn');
		} catch (err) {
			if (err instanceof ApiError && err.status === 409) {
				loadPending();
			}
			toast.error(err instanceof Error ? err.message : 'Failed to withdraw invite');
		} finally {
			setWithdrawingId(null);
		}
	};

	const where = applied?.city
		? `in ${applied.city}, ${applied.state}`
		: applied?.state
			? `in ${areas?.states.find(s => s.code === applied.state)?.name ?? applied.state}`
			: 'across all locations';

	return (
		<>
			{/* Search bar */}
			<div className="settings-card mb-6">
				<div className="settings-card__body">
					<div className="grid grid-cols-1 md:grid-cols-[180px_200px_1fr_auto] gap-3 items-end">
						<div className="provider-form__group mb-0">
							<label className="provider-form__label">State</label>
							<select
								className="provider-form__input"
								value={state}
								onChange={e => {
									setState(e.target.value);
									setCity('');
								}}
							>
								<option value="">All states</option>
								{areas?.states.map(s => (
									<option key={s.code} value={s.code}>
										{s.name} ({s.code})
									</option>
								))}
							</select>
						</div>
						<div className="provider-form__group mb-0">
							<label className="provider-form__label">City</label>
							<select
								className="provider-form__input"
								value={city}
								onChange={e => setCity(e.target.value)}
								disabled={!state}
							>
								<option value="">{state ? `All cities in ${state}` : 'All cities'}</option>
								{cities.map(c => (
									<option key={c} value={c}>
										{c}
									</option>
								))}
							</select>
						</div>
						<div className="provider-form__group mb-0">
							<label className="provider-form__label">Name, email or phone (optional)</label>
							<div className="staff-filters__search">
								<MagnifyingGlassIcon className="staff-filters__search-icon" />
								<input
									type="text"
									className="provider-form__input staff-filters__search-input"
									placeholder="e.g. Adaeze, adaeze@gmail.com, 718-555-0142"
									value={query}
									onChange={e => setQuery(e.target.value)}
									onKeyDown={e => {
										if (e.key === 'Enter') apply();
									}}
								/>
							</div>
						</div>
						<Button variant="primary" onClick={apply} isLoading={isSearching}>
							Apply
						</Button>
					</div>
					<p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
						Shows stylists who are listed as <strong>rotation staff</strong> on another salon
						owner&apos;s team. Pick a state and city and click <strong>Apply</strong> to browse
						everyone there, or add a name, email or phone number to find someone specific.
					</p>
				</div>
			</div>

			{/* Pending invites */}
			{pendingInvites.length > 0 && (
				<div className="settings-card mb-6">
					<div className="settings-card__body">
						<p className="provider-form__label mb-2">
							Pending invites ({pendingInvites.length})
						</p>
						<div className="flex flex-wrap gap-2">
							{pendingInvites.map(inv => (
								<div
									key={inv.id}
									className="flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 pl-3 pr-1 py-1 text-sm"
								>
									<span className="text-amber-900 dark:text-amber-100">
										{inv.staffFirstName} {inv.staffLastName} · {inv.commissionRate}% ·{' '}
										{inv.staffType === 'ON_CALL' ? 'Rotation' : 'Permanent'}
										{inv.startDate ? ` · from ${fmtDate(inv.startDate)}` : ''}
									</span>
									<button
										type="button"
										className="px-2 py-0.5 rounded-full text-xs font-semibold text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40 cursor-pointer disabled:opacity-50"
										onClick={() => withdraw(inv)}
										disabled={withdrawingId === inv.id}
									>
										{withdrawingId === inv.id ? '…' : 'Withdraw'}
									</button>
								</div>
							))}
						</div>
					</div>
				</div>
			)}

			{/* Results */}
			{error ? (
				<div className="py-12 text-center text-sm text-red-500">{error}</div>
			) : isSearching && results.length === 0 ? (
				<div className="py-16 text-center">
					<Spinner size="lg" className="mx-auto mb-3 text-purple-600" />
					<p className="text-sm text-gray-500 dark:text-gray-400">Searching…</p>
				</div>
			) : (
				<>
					<p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
						<strong>{results.length}</strong> rotation stylist{results.length === 1 ? '' : 's'} {where}
						{applied?.query ? ` matching “${applied.query}”` : ''}
					</p>

					{results.length === 0 ? (
						<div className="settings-card">
							<div className="settings-card__body text-center py-12">
								<MagnifyingGlassIcon className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
								<p className="text-sm text-gray-500 dark:text-gray-400">
									No rotation staff match {applied?.query ? 'that name, email or phone ' : 'those filters '}
									{where}. Try another city or clear the search.
								</p>
								{(applied?.state || applied?.city) && (
									<Button variant="secondary" size="sm" className="mt-4" onClick={searchEverywhere}>
										Search all locations
									</Button>
								)}
							</div>
						</div>
					) : (
						<div className="space-y-3">
							{results.map(s => {
								const invited = !!s.pendingInviteId;
								return (
									<div key={s.homeStaffId} className="settings-card">
										<div className="settings-card__body">
											<div className="flex flex-col sm:flex-row sm:items-start gap-4">
												<div
													className="w-12 h-12 rounded-xl text-white flex items-center justify-center text-base font-bold flex-shrink-0"
													style={{ background: avatarColor(s.homeStaffId) }}
												>
													{initials(s.firstName, s.lastName)}
												</div>
												<div className="min-w-0 flex-1">
													<div className="flex items-center gap-2 flex-wrap">
														<span className="text-base font-bold text-gray-900 dark:text-gray-100">
															{s.firstName} {s.lastName}
														</span>
														<Badge variant="warning">Rotation</Badge>
														{s.alreadyOnTeam && <Badge variant="success">On your team</Badge>}
													</div>
													{s.roleName && (
														<div className="text-sm text-gray-500 dark:text-gray-400">{s.roleName}</div>
													)}
													<div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-300 mt-1">
														{s.email && (
															<span className="inline-flex items-center gap-1">
																<EnvelopeIcon className="w-3.5 h-3.5" /> {s.email}
															</span>
														)}
														{s.phone && (
															<span className="inline-flex items-center gap-1">
																<PhoneIcon className="w-3.5 h-3.5" /> {s.phone}
															</span>
														)}
													</div>
													<div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-300 mt-1">
														<span>
															<strong>{s.jobsDone}</strong> jobs done
														</span>
														<span>
															<strong>{s.salonCount}</strong> salon{s.salonCount === 1 ? '' : 's'}
														</span>
													</div>
													<div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
														On rotation at{' '}
														{s.rotationAt.map(r => (
															<span
																key={r.providerId}
																className="inline-block ml-1 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
															>
																{r.businessName}
																{r.city ? ` • ${r.city}, ${r.state}` : ''}
															</span>
														))}
													</div>
													<div className="flex flex-wrap gap-1.5 mt-2">
														{s.areas.map(a => (
															<span
																key={`${a.city}|${a.state}`}
																className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
															>
																<MapPinIcon className="w-3 h-3" /> {a.city}, {a.state}
															</span>
														))}
													</div>
												</div>
												<div className="flex sm:flex-col gap-2 sm:items-stretch flex-shrink-0">
													<Button variant="secondary" size="sm" onClick={() => setProfileStaff(s)}>
														View Profile
													</Button>
													<Button
														variant="primary"
														size="sm"
														disabled={invited || s.alreadyOnTeam}
														onClick={() => setInviteStaff(s)}
													>
														{s.alreadyOnTeam ? 'On your team' : invited ? 'Invite Sent' : 'Invite & Set Commission'}
													</Button>
												</div>
											</div>
										</div>
									</div>
								);
							})}
						</div>
					)}
				</>
			)}

			<ProfileModal
				isOpen={profileStaff !== null}
				onClose={() => setProfileStaff(null)}
				staff={profileStaff}
				onInvite={s => {
					setProfileStaff(null);
					setInviteStaff(s);
				}}
			/>
			<InviteModal
				isOpen={inviteStaff !== null}
				onClose={() => setInviteStaff(null)}
				staff={inviteStaff}
				locationId={locationId}
				locations={locations}
				onSent={markInvited}
			/>
		</>
	);
};

export default RotationStaffFinder;
